<sh-page>
  <sh-pane
    #sidePane
    slot="left"
    behavior="condense"
    size="condensed"
    resizable
    (size-changed)="onPaneSizeChanged($event)"
    max-resize-width="25%"
    arrow-position="center"
    label="Query Filter"
  >
    <!-- Condensed state: ONLY the icon rail is visible. Clicking an icon
         expands the pane and opens that section exclusively. -->
    <div class="pane-icon-rail" slot="condensed">
      <sh-icon
        id="tt-rail-filter"
        icon="filter"
        button
        class="rail-icon"
        [class.active]="expandedSections.filter"
        (click)="openSectionFromRail('filter')"
        (keydown.enter)="openSectionFromRail('filter')">
      </sh-icon>
      <sh-tooltip delay target="tt-rail-filter" label="Query Filter" placement="right" variation="short"></sh-tooltip>

      <sh-icon
        id="tt-rail-fav-query"
        icon="documents-outline"
        button
        class="rail-icon"
        [class.active]="expandedSections.favoriteQuery"
        (click)="openSectionFromRail('favoriteQuery')"
        (keydown.enter)="openSectionFromRail('favoriteQuery')">
      </sh-icon>
      <sh-tooltip delay target="tt-rail-fav-query" label="Favorite Query" placement="right" variation="short"></sh-tooltip>

      <sh-icon
        id="tt-rail-fav-template"
        icon="program-template"
        button
        class="rail-icon"
        [class.active]="expandedSections.favoriteTemplate"
        (click)="openSectionFromRail('favoriteTemplate')"
        (keydown.enter)="openSectionFromRail('favoriteTemplate')">
      </sh-icon>
      <sh-tooltip delay target="tt-rail-fav-template" label="Favorite Template" placement="right" variation="short"></sh-tooltip>
    </div>

    <!-- Expanded state body -->
    <div class="pane-column-section" *ngIf="isConfigLoading">
      <sh-spinner></sh-spinner>
    </div>

    <div class="pane-column-section" *ngIf="!isConfigLoading && configLoadError">
      <sh-text color="critical">{{ configLoadError }}</sh-text>
    </div>

    <!-- Global search (applies across all sections) -->
    <sh-search
      label="Search to filter query, query group..."
      [value]="globalSearchText"
      (value-changed)="onGlobalSearchChange($event)">
    </sh-search>

    <!-- Query Filter -->
    <sh-accordion
      icon="filter"
      label="Query Filter"
      [expanded]="expandedSections.filter"
      (expanded-changed)="onAccordionExpandedChange('filter', $event)"
      *ngIf="!isConfigLoading && !configLoadError">

      <!-- Selected vs Available tabs -->
      <div class="filter-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="filter-tab"
          [class.active]="activeFilterTab === 'selected'"
          [attr.aria-selected]="activeFilterTab === 'selected'"
          (click)="setActiveFilterTab('selected')">
          Selected ({{ selectedColumns.length }})
        </button>
        <button
          type="button"
          role="tab"
          class="filter-tab"
          [class.active]="activeFilterTab === 'available'"
          [attr.aria-selected]="activeFilterTab === 'available'"
          (click)="setActiveFilterTab('available')">
          Available ({{ availableColumns.length }})
        </button>
      </div>

      <!-- Selected list -->
      <div class="column-list" *ngIf="activeFilterTab === 'selected'">
        <div class="column-group">
          <div class="column-option" *ngFor="let column of filteredSelectedColumns">
            <sh-checkbox [active]="true" (checked)="onSelectedColumnChange(column, $event)"></sh-checkbox>
            <span class="column-label">{{ column }}</span>
          </div>
        </div>
        <div class="empty-state" *ngIf="filteredSelectedColumns.length === 0">
          <sh-icon icon="filter" size="s"></sh-icon>
          <sh-text size="body-1" color="secondary">No filters selected yet</sh-text>
        </div>
      </div>

      <!-- Available list -->
      <div class="column-list" *ngIf="activeFilterTab === 'available'">
        <div class="column-group">
          <div class="column-option" *ngFor="let column of filteredAvailableColumns">
            <sh-checkbox (checked)="onAvailableColumnChange(column, $event)"></sh-checkbox>
            <span class="column-label">{{ column }}</span>
          </div>
        </div>
        <div class="empty-state" *ngIf="filteredAvailableColumns.length === 0">
          <sh-icon icon="filter" size="s"></sh-icon>
          <sh-text size="body-1" color="secondary">No filters available</sh-text>
        </div>
      </div>

      <div class="submit-container">
        <sh-button id="tt-savecolumns" color="secondary" [label]="isSavingColumns ? 'Saving...' : 'Save'" [disabled]="isSavingColumns"
          (click)="onSubmitColumns()" (keydown.enter)="onSubmitColumns()">
        </sh-button>
        <sh-tooltip delay target="tt-savecolumns" [label]="'configureColumns.saveButtonTooltip' | translate" placement="top" variation="long"></sh-tooltip>
      </div>
    </sh-accordion>

    <!-- Favorite Query (HTML only) -->
    <sh-accordion
      icon="documents-outline"
      label="Favorite Query"
      [expanded]="expandedSections.favoriteQuery"
      (expanded-changed)="onAccordionExpandedChange('favoriteQuery', $event)"
      (click)="openFavoritesModal('favoriteQuery', $event)"
      (keydown.enter)="onAccordionKeydown('favoriteQuery', $event)"
      (keydown.space)="onAccordionKeydown('favoriteQuery', $event)">

      <div class="favorites-list">
        <div class="favorite-item" *ngFor="let query of filteredFavoriteQueries">
          <sh-icon
            class="favorite-star"
            [icon]="query.isFavourite ? 'favorite' : 'favorite-outline'"
            size="s"
            button
            (click)="toggleFavorite(query, $event)"
            (keydown.enter)="toggleFavorite(query, $event)">
          </sh-icon>
          <span class="favorite-name">{{ query.name }}</span>
          <sh-icon
            class="favorite-import"
            icon="import"
            size="s"
            button
            (click)="importFavorite(query, $event, false)"
            (keydown.enter)="importFavorite(query, $event, false)">
          </sh-icon>
        </div>

        <div class="empty-state" *ngIf="filteredFavoriteQueries.length === 0">
          <sh-icon icon="documents-outline" size="s"></sh-icon>
          <sh-text size="body-1" color="secondary">No favorites yet</sh-text>
        </div>
      </div>
    </sh-accordion>

    <!-- Favorite Template (HTML only) -->
    <sh-accordion
      icon="program-template"
      label="Favorite Template"
      [expanded]="expandedSections.favoriteTemplate"
      (expanded-changed)="onAccordionExpandedChange('favoriteTemplate', $event)"
      (click)="openFavoritesModal('favoriteTemplate', $event)"
      (keydown.enter)="onAccordionKeydown('favoriteTemplate', $event)"
      (keydown.space)="onAccordionKeydown('favoriteTemplate', $event)">

      <div class="favorites-list">
        <div class="favorite-item" *ngFor="let template of filteredFavoriteTemplates">
          <sh-icon
            class="favorite-star"
            [icon]="template.isFavourite ? 'favorite' : 'favorite-outline'"
            size="s"
            button
            (click)="toggleFavorite(template, $event)"
            (keydown.enter)="toggleFavorite(template, $event)">
          </sh-icon>
          <span class="favorite-name">{{ template.name }}</span>
          <sh-icon
            class="favorite-import"
            icon="import"
            size="s"
            button
            (click)="importFavorite(template, $event, true)"
            (keydown.enter)="importFavorite(template, $event, true)">
          </sh-icon>
        </div>

        <div class="empty-state" *ngIf="filteredFavoriteTemplates.length === 0">
          <sh-icon icon="program-template" size="s"></sh-icon>
          <sh-text size="body-1" color="secondary">No favorites yet</sh-text>
        </div>
      </div>
    </sh-accordion>
  </sh-pane>


  <sh-card class="main-pane-content">
    <div class="content-section">
      <app-filter-criteria (favoriteChanged)="loadFavorites()"></app-filter-criteria>
      <app-eligible-systems-table></app-eligible-systems-table>
    </div>
  </sh-card>
</sh-page>